import React from 'react';
import { Grid, FormControl, InputLabel, Select, MenuItem, Box, Typography } from '@mui/material';
import TextInput from '../../../sharedModules/form/components/TextInput';
import InspectionRadioGroup from './InspectionRadioGroup';
import { conditionOptions, conditionWithNaOptions, yesNoOptions, plumbingAgeOptions } from '../config/options';

export interface PlumbingSystemValues {
  id: string;
  location: string;
  manufacturer: string;
  age: string;
  ageCondition: string;
  systemType: string;
  overallVisualCondition: string;
  expansionTankNa: boolean;
  expansionTankCondition: string;
  visibleLeaksOrRust: string;
  properClearance: string;
  recommendedAnnualMaintenance: string;
  installedCoupon: string;
  laundryTray: string;
  notes: string;
}

export function createDefaultPlumbingSystem(): PlumbingSystemValues {
  return {
    id: crypto.randomUUID(),
    location: '',
    manufacturer: '',
    age: '',
    ageCondition: '',
    systemType: '',
    overallVisualCondition: '',
    expansionTankNa: false,
    expansionTankCondition: '',
    visibleLeaksOrRust: '',
    properClearance: '',
    recommendedAnnualMaintenance: '',
    installedCoupon: '',
    laundryTray: '',
    notes: '',
  };
}

/** @deprecated Use createDefaultPlumbingSystem() to get a fresh UUID each time. */
export const defaultPlumbingSystem: PlumbingSystemValues = createDefaultPlumbingSystem();

interface PlumbingSystemFieldsProps {
  values: PlumbingSystemValues;
  onChange: (field: keyof PlumbingSystemValues, value: any) => void;
  manufacturers: { value: string; label: string }[];
}

const subHeaderSx = {
  fontSize: '14px',
  fontWeight: 600,
  color: '#3d5a80',
  bgcolor: '#e8eef4',
  px: 2,
  py: 0.75,
  borderRadius: 1,
  mb: 1.5,
};

const getAgeColor = (value: string): string | undefined => {
  if (!value || value === 'n_a') return undefined;
  const map: Record<string, string> = { '1_6': '#4caf50', '6_9': '#ff9800', '10_15': '#f44336' };
  return map[value];
};

const getAgeCondition = (value: string): string => {
  if (!value || value === 'n_a') return '';
  const map: Record<string, string> = { '1_6': 'good', '6_9': 'needs_attention', '10_15': 'requires_service' };
  return map[value] || '';
};

const PlumbingSystemFields: React.FC<PlumbingSystemFieldsProps> = ({ values, onChange, manufacturers }) => (
  <Box>
    <Grid container spacing={2} sx={{ mb: 2 }}>
      <Grid item xs={12} md={4}>
        <FormControl fullWidth variant="filled">
          <InputLabel>Age</InputLabel>
          <Select
            value={values.age}
            onChange={(e) => {
              const val = e.target.value as string;
              onChange('age', val);
              onChange('ageCondition', getAgeCondition(val));
            }}
            sx={{
              ...(getAgeColor(values.age) && {
                bgcolor: getAgeColor(values.age),
                color: '#fff',
                '&.Mui-focused': { bgcolor: getAgeColor(values.age) },
                '& .MuiSelect-icon': { color: '#fff' },
                '& .MuiFilledInput-input': { color: '#fff' },
              }),
            }}
          >
            {plumbingAgeOptions.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} md={4}>
        <TextInput label="Location" value={values.location} valueChanged={(val) => onChange('location', val)} />
      </Grid>
    </Grid>

    <Typography sx={subHeaderSx}>Type</Typography>
    <Box sx={{ mb: 2 }}>
      <InspectionRadioGroup
        radioOptions={[
          { label: 'Water Heater', value: 'tank_water_heater' },
          { label: 'Tankless Water Heater', value: 'tankless_water_heater' },
        ]}
        value={values.systemType}
        setValue={(val) => onChange('systemType', val)}
      />
    </Box>

    <Box sx={{ mb: 2 }}>
      <FormControl fullWidth variant="filled">
        <InputLabel>Manufacturer</InputLabel>
        <Select value={values.manufacturer} onChange={(e) => onChange('manufacturer', e.target.value as string)}>
          {manufacturers.map((opt) => (
            <MenuItem key={opt.value} value={opt.label}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>

    <Typography sx={subHeaderSx}>Inspection Details</Typography>
    <Grid container spacing={2} sx={{ mb: 2 }}>
      <Grid item xs={12}>
        <InspectionRadioGroup
          label="Overall/Visual Condition"
          radioOptions={conditionOptions}
          value={values.overallVisualCondition}
          setValue={(val) => onChange('overallVisualCondition', val)}
        />
      </Grid>
      {values.systemType === 'tank_water_heater' && (
        <Grid item xs={12}>
          <InspectionRadioGroup
            label="Expansion Tank"
            radioOptions={conditionWithNaOptions}
            value={values.expansionTankCondition}
            setValue={(val) => onChange('expansionTankCondition', val)}
          />
        </Grid>
      )}
      <Grid item xs={12}>
        <InspectionRadioGroup
          label="Visible Leaks or Rust"
          radioOptions={yesNoOptions}
          value={values.visibleLeaksOrRust}
          setValue={(val) => onChange('visibleLeaksOrRust', val)}
        />
      </Grid>
      <Grid item xs={12}>
        <InspectionRadioGroup
          label="Proper Clearance Around Equipment"
          radioOptions={yesNoOptions}
          value={values.properClearance}
          setValue={(val) => onChange('properClearance', val)}
        />
      </Grid>
      {values.systemType === 'tankless_water_heater' && (
        <Grid item xs={12}>
          <InspectionRadioGroup
            label="Recommended Annual Maintenance"
            radioOptions={yesNoOptions}
            value={values.recommendedAnnualMaintenance}
            setValue={(val) => onChange('recommendedAnnualMaintenance', val)}
          />
        </Grid>
      )}
      {values.systemType === 'tank_water_heater' && (
        <Grid item xs={12}>
          <InspectionRadioGroup
            label="Installed $50 Coupon"
            radioOptions={yesNoOptions}
            value={values.installedCoupon}
            setValue={(val) => onChange('installedCoupon', val)}
          />
        </Grid>
      )}
      <Grid item xs={12}>
        <InspectionRadioGroup
          label="Laundry Tray"
          radioOptions={conditionWithNaOptions}
          value={values.laundryTray}
          setValue={(val) => onChange('laundryTray', val)}
        />
      </Grid>
    </Grid>

    <TextInput
      label="Plumbing Notes"
      value={values.notes}
      valueChanged={(val) => onChange('notes', val)}
      multiline
      rows={3}
    />
  </Box>
);

export default PlumbingSystemFields;
